import { jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useEffect, useState, useRef, forwardRef, useCallback } from 'react';

function printDeprecatedPropWarning(componentName, propName, message) {
  console.warn(`Deprecated prop '${propName}' in '<${componentName} />'. ${message ? message : ''}`);
}
function printUnsupportedPropWarning(componentName, propName, message) {
  console.warn(`Unsupported prop '${propName}' in '<${componentName} />'. ${message ? message : ''}`);
}

function unextendStyle(style, functionParam) {
  if (typeof style === 'function') {
    // @ts-expect-error: Argument of type 'T | undefined' is not assignable to parameter of type 'T'.
    return style(functionParam);
  }
  return style;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Inspired by the `when` expression in Kotlin, this is an alternative to
 * `switch` statements, for when you want to use switch-like features in an expression.
 * Handy in more functional-like programming. It also offers more flexibility than
 * a regular `switch` statement.
 *
 * @param branches
 * A list of tuples, where each tuple consists of an expression and a value.
 * The expression will be evaluated for truthiness.
 *
 * @return
 * The value of the first branch with an expression that evaluates to truthy,
 * or undefined.
 *
 * @example
 * // Without `when`:
 * const value = 1;
 * let otherValue;
 * switch (value) {
 *   case 0:
 *     otherValue = 'foo';
 *     function0();
 *     break;
 *   case 1:
 *     otherValue = 'bar';
 *     function1();
 *     break;
 * }
 *
 * // With `when`:
 * const value = 1;
 * const otherValue = when(
 *   [value === 0, () => {
 *     function0();
 *     return 'foo';
 *   }],
 *   [value === 1, () => {
 *     function1();
 *     return 'bar';
 *   }],
 * );
 *
 * @see https://kotlinlang.org/docs/control-flow.html#when-expression
 */
function when(...branches) {
  const branch = branches.find(([expr]) => {
    let use = false;
    if (typeof expr === 'function') {
      use = !!expr();
    } else {
      use = !!expr;
    }
    return use;
  });
  return branch === null || branch === void 0 ? void 0 : branch[1]();
}

/**
 * Show or hide a dialog element.
 * This hook is an abstraction on top of the imperative `<dialog />` API.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 */
function useShowDialog({
  dialog,
  disabled,
  mode,
  open
}) {
  useEffect(() => {
    if (disabled) return;
    setTimeout(() => {
      var _a, _b, _c, _d, _e;
      // By wrapping with a timeout, we ensure that any external show/hide logic
      // of the dialog (e.g. adding/removing it from the DOM) has the chance to
      // run before we try to show/hide it.
      if (open) {
        if (!((_a = dialog.current) === null || _a === void 0 ? void 0 : _a.open)) {
          mode === 'modal' ? (_b = dialog.current) === null || _b === void 0 ? void 0 : _b.showModal() : (_c = dialog.current) === null || _c === void 0 ? void 0 : _c.show();
        }
      } else {
        if ((_d = dialog.current) === null || _d === void 0 ? void 0 : _d.open) {
          (_e = dialog.current) === null || _e === void 0 ? void 0 : _e.close();
        }
      }
    }, 5); // 0 works for all browsers except Safari (tested in 17.4.1).
  }, [dialog, disabled, mode, open]);
}

/**
 * Takes a (sync) callback function and returns a throttled version of it.
 *
 * @example
 * const handleMouseMoveThrottled = throttle((event: MouseEvent) => {
 *   console.log(event.pageX, event.pageY);
 * }, 500);
 * document.addEventListener('mousemove', handleMouseMoveThrottled);
 */
function throttle(callback, throttleMs) {
  let prevCallTimestamp = 0;
  let timeoutId;
  function throttled(...args) {
    const sincePrevCall = Date.now() - prevCallTimestamp;
    clearTimeout(timeoutId);
    function callbackWrapper() {
      const result = callback(...args);
      prevCallTimestamp = Date.now();
      return result;
    }
    // Either we're free to call, or we need to ensure last (most likely in-between 'frames') call gets fired.
    if (sincePrevCall >= throttleMs) {
      return callbackWrapper();
    } else {
      timeoutId = setTimeout(callbackWrapper, throttleMs - sincePrevCall);
    }
  }
  return throttled;
}

var styles$1 = {"popover":"popover___15nUq","popover__dialog":"popover__dialog___HpONW"};

/**
 * Given an element `el`, return it's current px transform along the X and Y axes.
 */
function transformXY(el) {
  const transform = getComputedStyle(el).transform; // 'matrix(1, 0, 0, 1, -25, 50)'
  if (transform.includes('matrix(')) {
    const matrix = transform.replace('matrix(', '').replace(')', '').split(', ').map(n => parseFloat(n));
    return [matrix[4], matrix[5]];
  }
  return [0, 0];
}

function invert(number) {
  return number * -1;
}
/**
 * Given a number as a string, invert the number and return the new string.
 */
function invertNumberString(numberStr) {
  return numberStr.includes('-') ? numberStr.replace('-', '') : `-${numberStr.replace('+', '')}`;
}

/**
 * Given `anchor`, `dialog` and `popover` elements, and some positioning metadata,
 * return a tuple of `[x, y]` where both members dictates whether the `dialog`
 * is outside the browser viewport in the corresponding axis, and should have
 * it's position inverted to be visible in the viewport.
 */
function shouldInverseXYTo({
  anchor,
  anchorOriginXY,
  dialog,
  inversedXY,
  popover
}) {
  var _a, _b;
  const {
    innerHeight: windowHeight,
    innerWidth: windowWidth
  } = window;
  const {
    bottom: anchorBottom = 0,
    left: anchorLeft = 0,
    right: anchorRight = 0,
    top: anchorTop = 0
  } = ((_a = anchor.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) || {};
  const {
    bottom: dialogBottom = 0,
    height: dialogHeight = 0,
    left: dialogLeft = 0,
    right: dialogRight = 0,
    top: dialogTop = 0,
    width: dialogWidth = 0
  } = ((_b = dialog.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) || {};
  const transformXY$1 = popover.current ? transformXY(popover.current) : [0, 0];
  const isOutsideBounds = {
    top: dialogTop < 0,
    bottom: dialogBottom > windowHeight,
    left: dialogLeft < 0,
    right: dialogRight > windowWidth
  };
  // prettier-ignore
  const wouldBeOutsideBoundsAfterInverting = {
    top: inversedXY[1] ? anchorTop + anchorOriginXY[1] + invert(transformXY$1[1]) < 0 : anchorBottom - anchorOriginXY[1] - dialogHeight - transformXY$1[1] < 0,
    bottom: inversedXY[1] ? anchorTop + anchorOriginXY[1] + dialogHeight + invert(transformXY$1[1]) > windowHeight : anchorBottom - anchorOriginXY[1] - transformXY$1[1] > windowHeight,
    left: inversedXY[0] ? anchorLeft + anchorOriginXY[0] + invert(transformXY$1[0]) < 0 : anchorRight - anchorOriginXY[0] - dialogWidth - transformXY$1[0] < 0,
    right: inversedXY[0] ? anchorLeft + anchorOriginXY[0] + dialogWidth + invert(transformXY$1[0]) > windowWidth : anchorRight - anchorOriginXY[0] - transformXY$1[0] > windowWidth
  };
  // prettier-ignore
  const canInvertTo = {
    top: !wouldBeOutsideBoundsAfterInverting.top && (inversedXY[1] ? true : anchorBottom - anchorOriginXY[1] - transformXY$1[1] >= dialogHeight),
    bottom: !wouldBeOutsideBoundsAfterInverting.bottom && (inversedXY[1] ? true : anchorBottom - anchorOriginXY[1] - transformXY$1[1] >= dialogHeight),
    left: !wouldBeOutsideBoundsAfterInverting.left && (inversedXY[0] ? true : anchorRight - anchorOriginXY[0] - transformXY$1[0] >= dialogWidth),
    right: !wouldBeOutsideBoundsAfterInverting.right && (inversedXY[0] ? true : anchorRight - anchorOriginXY[0] - transformXY$1[0] >= dialogWidth)
  };
  // prettier-ignore
  const shouldInverseX = when([isOutsideBounds.left && canInvertTo.right && !wouldBeOutsideBoundsAfterInverting.left && !inversedXY[0], function invertPositionToRight() {
    return 'right';
  }], [isOutsideBounds.right && canInvertTo.left && !wouldBeOutsideBoundsAfterInverting.right && !inversedXY[0], function invertPositionToLeft() {
    return 'left';
  }], [inversedXY[0] === 'right' && (isOutsideBounds.right || isOutsideBounds.left || canInvertTo.left) || inversedXY[0] === 'left' && (isOutsideBounds.right || isOutsideBounds.left || canInvertTo.right), function resetPositionX() {
    return null;
  }], [true, () => inversedXY[0]]);
  // prettier-ignore
  const shouldInverseY = when([isOutsideBounds.top && canInvertTo.bottom && !wouldBeOutsideBoundsAfterInverting.top && !inversedXY[1], function invertPositionToBottom() {
    return 'bottom';
  }], [isOutsideBounds.bottom && canInvertTo.top && !wouldBeOutsideBoundsAfterInverting.bottom && !inversedXY[1], function invertPositionToTop() {
    return 'top';
  }], [inversedXY[1] === 'bottom' && (isOutsideBounds.bottom || isOutsideBounds.top || canInvertTo.top) || inversedXY[1] === 'top' && (isOutsideBounds.bottom || isOutsideBounds.top || canInvertTo.bottom), function resetPositionY() {
    return null;
  }], [true, () => inversedXY[1]]);
  return [shouldInverseX, shouldInverseY];
}

const NUMBER_REGEX = /[-+]?[0-9]+(\.[0-9]+)?/g;
/**
 * Let's you anchor an element to another element, i.e. position an element
 * relative to another.
 * Displays an unstyled popover/nonmodal dialog that will automatically
 * reposition itself based on available viewport space.
 * This component is used by other components, and in most cases, consumers
 * should not have to use this directly.
 *
 * @example
 * const [open, setOpen] = useState(false);
 * const anchor = useRef<HTMLButtonElement>(null);
 *
 * <AnchoredPopover anchor={anchor} open={open}>
 *   <p>I will be positioned relative to `anchor`</p>
 * </AnchoredPopover>
 *
 * <button onClick={() => setOpen(true)} ref={anchor}>Open popover</button>
 */
function AnchoredPopover(_a) {
  var _b;
  var {
      anchor,
      anchorOrigin = '0%, 0%',
      children,
      className,
      onInvert,
      open = false,
      ref_,
      slotProps,
      style,
      transformOrigin = '0%, 0%'
    } = _a,
    rest = __rest(_a, ["anchor", "anchorOrigin", "children", "className", "onInvert", "open", "ref_", "slotProps", "style", "transformOrigin"]);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [originX, setOriginX] = useState('left');
  const [originY, setOriginY] = useState('top');
  const [remeasureInitialPosition, setRemeasureInitialPosition] = useState(false);
  const [transform, setTransform] = useState(() => transformOrigin.replace(NUMBER_REGEX, invertNumberString));
  const originalTransform = useRef('0%, 0%');
  const inversedX = useRef(null);
  const inversedY = useRef(null);
  const popover = useRef(null);
  const dialogRef = useRef(null);
  const dialog = ref_ || dialogRef;
  useEffect(() => {
    // Store value so we can easily revert.
    originalTransform.current = transformOrigin.replace(NUMBER_REGEX, invertNumberString);
    setTransform(originalTransform.current);
  }, [transformOrigin]);
  useShowDialog({
    dialog: dialog,
    mode: 'nonmodal',
    open
  });
  useEffect(() => {
    /**
     * Must `useEffect` instead of `useLayoutEffect` to not depend on `anchor`
     * being placed before `AnchoredPopover` in VDOM/DOM.
     */
    if (!window) return;
    let animationTimeoutId = null;
    function bindToAnchorPosition(animate) {
      if (!anchor.current) return;
      const {
        bottom: anchorBottom,
        height: anchorHeight,
        left: anchorLeft,
        right: anchorRight,
        top: anchorTop,
        width: anchorWidth
      } = anchor.current.getBoundingClientRect();
      const anchorOriginXY = anchorOrigin.split(', ').map((coordinate, i) => {
        const base = when([i === 0, () => anchorWidth], [i === 1, () => anchorHeight], [true, () => 0]);
        return coordinate.includes('%') ? base * (parseFloat(coordinate) / 100) : parseFloat(coordinate);
      });
      const {
        innerHeight: windowHeight,
        innerWidth: windowWidth
      } = window;
      if (dialog.current && popover.current) {
        const [shouldInverseXTo, shouldInverseYTo] = shouldInverseXYTo({
          anchor,
          anchorOriginXY,
          dialog,
          inversedXY: [inversedX.current, inversedY.current],
          popover
        });
        inversedX.current = shouldInverseXTo;
        inversedY.current = shouldInverseYTo;
      }
      const newX = (inversedX.current ? windowWidth - anchorRight : anchorLeft) + anchorOriginXY[0];
      const newY = (inversedY.current ? windowHeight - anchorBottom : anchorTop) + anchorOriginXY[1];
      const inverseTransformX = when([inversedX.current, () => transformOrigin.split(', ')[0]], [true, () => originalTransform.current.split(', ')[0]]);
      const inverseTransformY = when([inversedY.current, () => transformOrigin.split(', ')[1]], [true, () => originalTransform.current.split(', ')[1]]);
      if (animate && popover.current) {
        animationTimeoutId && clearTimeout(animationTimeoutId);
        popover.current.style.transition = 'all .1s ease-out';
      }
      setOriginX(inversedX.current ? 'right' : 'left');
      setOriginY(inversedY.current ? 'bottom' : 'top');
      setX(newX);
      setY(newY);
      setTransform(`${inverseTransformX}, ${inverseTransformY}`);
      setRemeasureInitialPosition(true);
      animationTimeoutId = setTimeout(() => {
        popover.current && (popover.current.style.transition = '');
      }, 200);
    }
    const bindToAnchorPositionThrottled = throttle(() => {
      requestAnimationFrame(() => bindToAnchorPosition(true));
    }, 200);
    if (open) {
      bindToAnchorPosition();
      window.addEventListener('resize', bindToAnchorPositionThrottled, {
        passive: true
      });
      window.addEventListener('scroll', bindToAnchorPositionThrottled, {
        // `capture: true` ignores scrolling in other elements than `window`.
        capture: true,
        passive: true
      });
    }
    return () => {
      window.removeEventListener('resize', bindToAnchorPositionThrottled);
      window.removeEventListener('scroll', bindToAnchorPositionThrottled, {
        capture: true
      });
    };
  }, [anchor, anchorOrigin, open, dialog, remeasureInitialPosition, transformOrigin]);
  useEffect(() => {
    onInvert === null || onInvert === void 0 ? void 0 : onInvert(originX === 'right', originY === 'bottom');
  }, [onInvert, originX, originY]);
  return jsx("div", Object.assign({}, slotProps === null || slotProps === void 0 ? void 0 : slotProps.popover, {
    className: `${styles$1.popover} ${(_b = slotProps === null || slotProps === void 0 ? void 0 : slotProps.popover) === null || _b === void 0 ? void 0 : _b.className}`,
    ref: popover,
    style: {
      [originY]: y,
      [originX]: x,
      transform: `translate(${transform})`
    },
    children: jsx("dialog", Object.assign({
      className: classNames(styles$1.popover__dialog, className),
      ref: dialog,
      style: unextendStyle(style)
    }, rest, {
      children: children
    }))
  }));
}

function printDeprecatedComponentWarning(componentName, message) {
  console.warn(`Deprecated component '<${componentName} />'. ${message ? message : ''}`);
}

var styles = {"sr":"sr___8m6Uf"};

/**
 * Serves the same purpose as a regular `input[type="hidden"]`, but without the
 * quirks: this works with `change`, `input` and `focus` events.
 */
const HiddenInput = /*#__PURE__*/forwardRef(function HiddenInput(props, ref) {
  var _a, _b;
  return jsx("input", Object.assign({}, props, {
    "aria-hidden": (_a = props['aria-hidden']) !== null && _a !== void 0 ? _a : true,
    // Note #1: `change` and `input` events do not trigger on `[type="hidden"]`
    // so we hide with CSS instead.
    // See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/hidden
    // Note #2: `focus` events don't trigger on elements with the `hidden`
    // attribute, `display: none` or `visibility: hidden`, so we hide with
    // "screen reader only styles" and `aria-hidden="true"`, to allow
    // programmtically setting focus to `ref`.
    // See https://react-hook-form.com/docs/useform/setfocus
    className: `${styles.sr} ${props.className || ''}`,
    ref: ref,
    tabIndex: (_b = props.tabIndex) !== null && _b !== void 0 ? _b : -1
  }));
});

/**
 * By default, changing the value of an input element (e.g. an `<input />` or
 * `<textarea />`) programmatically via the `value` prop or the
 * `HTMLInputElement.value` setter, won't trigger it's corresponding React event
 * handler (e.g. `onChange`). This is a workaround to manually trigger the
 * React event handler. This is required, for example, in order to support third
 * party libraries that depend on React event handlers being fired, such as
 * React Hook Form.
 *
 * Note #1: In most cases, you won't need to use this function. You'll only need to
 * use this if you're programmatically changing the value of an input element
 * via other means than it's regular event handlers (e.g. `onChange`) + `setState`,
 * and you need to trigger these event handlers manually.
 *
 * Note #2: For `onFocus` and `onBlur` events, React uses the native `focusin`
 * and `focusout` events under the hood, so if you want to manually trigger
 * `onFocus` or `onBlur` event handlers, you should use `focusin` or `focusout`
 * as the `eventType` argument.
 *
 * @example
 *
 * ```tsx
 * const [value, setValue] = useState<string>('');
 * const inputRef = useRef<HTMLInputElement>(null);
 *
 * useEffect(() => {
 *   if (inputRef.current) {
 *     // Dispatching a change event, which in turn will trigger the React
 *     // `onChange` event handler for `inputRef.current`.
 *     dispatchEvent(inputRef.current, 'change', { bubbles: true });
 *   }
 * }, [value]);
 *
 * ...
 *
 * <form>
 *   {
 *     // A visible input that stores a formatted value.
 *     // This is the input the user interacts with, but we don't want to submit
 *     // this input with the form, as it's value is formatted in a non-computer
 *     // friendly way.
 *   }
 *   <input
 *     name={undefined}
 *     onChange={(e) => {
 *       setValue(e.target.value);
 *     }}
 *     value={format(value)}
 *   />
 *
 *   {
 *     // A hidden input that stores the actual value.
 *     // This is the input we want to submit with the form.
 *   }
 *   <input
 *     name="name"
 *     onChange={() => console.log(
 *       "Won't trigger when this element's `value` prop changes," +
 *       "so we need to trigger it with `useEffect` + `dispatchEvent` instead."
 *     )}
 *     ref={inputRef}
 *     style={{ display: 'none' }}
 *     // Note: `change` and `input` events do not trigger on `[type="hidden"]`.
 *     // If you use those events, use another method (e.g. CSS) to hide the input.
 *     // See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/hidden
 *     // type='hidden'
 *     value={value}
 *   />
 * </form>
 * ```
 *
 * @see https://hustle.bizongo.in/simulate-react-on-change-on-controlled-components-baa336920e04
 * @see https://legacy.reactjs.org/blog/2020/08/10/react-v17-rc.html#aligning-with-browsers
 * @see https://github.com/facebook/react/issues/19606
 */
function dispatchEvent(el, eventType, eventInitDict) {
  var _a;
  if (typeof eventType !== 'string') return;
  if (eventType === 'change' && 'value' in el) {
    // React hijacks the `value` setter, so we need to use the native setter
    // when setting value back to original value.
    const nativeValueSetter = (_a = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), 'value')) === null || _a === void 0 ? void 0 : _a.set;
    const originalValue = el.value;
    // In the case of `change` events, the event won't trigger if the value
    // hasn't changed, so we temporarily change it.
    el.value = tempValue(el);
    // We reset the value back to it's original value. A 'value change' has now
    // occured, and the actual event can be dispatched.
    nativeValueSetter === null || nativeValueSetter === void 0 ? void 0 : nativeValueSetter.call(el, originalValue);
  }
  // Dispatch the actual event, which will trigger corresponding React event handlers.
  const event = new Event(eventType, eventInitDict);
  el.dispatchEvent(event);
}
function tempValue(el) {
  switch (el.type) {
    case 'date':
    case 'datetime':
      // date inputs require special formatting to be valid.
      return el.value ? '' : '0001-01-01';
    default:
      return 'REQUIRED_TO_TRIGGER_CHANGE_EVENT_' + Math.random();
  }
}

/**
 * Given an Element `from`, traverse to a sibling by `to` number of steps and
 * return that sibling if it exists.
 * Note: To traverse backwards, set `to` to a negative number.
 */
function traverseToSibling(from, to) {
  let sibling = from;
  while (sibling && to !== 0) {
    sibling = to > 0 ? sibling === null || sibling === void 0 ? void 0 : sibling.nextElementSibling : sibling === null || sibling === void 0 ? void 0 : sibling.previousElementSibling;
    to += to > 0 ? -1 : 1;
  }
  return to === 0 ? sibling : null;
}

/**
 * Merges multiple refs into a single ref. Returns a function that can be used
 * in a component's `ref` prop.
 *
 * @example
 * return <input ref={mergeRefs(ref1, ref2, ref3)} />
 */
function mergeRefs(...refs) {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (typeof ref === 'object' && ref) {
        ref.current = value;
      }
    });
  };
}
/**
 * Merges multiple refs into a single ref. Returns a memoized function that can
 * be used in a component's `ref` prop.
 *
 * @example
 * const ref = useMergeRefs(ref1, ref2, ref3);
 * ...
 * return <input ref={ref} />
 */
function useMergeRefs(...refs) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(mergeRefs(...refs), refs);
}

/**
 * Run callback when a click event occurs outside of the provided `ref`.
 * Useful for closing dialogs, dropdowns, etc.
 *
 * Note: If you have an "open dialog" button, clicking this would by default
 * register as a click outside the dialog, and potentially close the dialog
 * before it's ever opened. To prevent this, use the optional `disabled`
 * parameter to control when the outside click handler should be enabled.
 *
 * @example
 *
 * const [open, setOpen] = useState(true);
 * const ref = useRef<HTMLDialogElement>(null);
 *
 * const onClickOutside = useOnClickOutside(ref, !open); // Note: second argument is optional.
 * onClickOutside((event) => {
 *   console.log('You clicked outside the open dialog');
 *   console.log(event); // The original `MouseEvent`.
 *   setOpen(false);
 * });
 *
 * ...
 *
 * <dialog ref={ref} open={open}>
 */
function useOnClickOutside(ref, disabled = false) {
  const cb = useRef();
  function onClickOutside(callback) {
    cb.current = callback;
  }
  useEffect(() => {
    if (!document) return;
    function handleClickOutside(event) {
      var _a;
      if (ref.current && (event === null || event === void 0 ? void 0 : event.target).isConnected && !ref.current.contains(event === null || event === void 0 ? void 0 : event.target)) {
        (_a = cb.current) === null || _a === void 0 ? void 0 : _a.call(cb, event);
      }
    }
    if (!disabled) {
      setTimeout(() => {
        /**
         * By wrapping with a timeout, we ensure that any external click logic
         * (e.g. 'click this button to show/hide a dialog') has the chance to
         * run before we try to act on the click. Failing to do so could cause
         * the outside click to interfere with the external click logic.
         * For a more detailed explanation, see
         * {@link https://codepen.io/olefjaerestad/pen/XWwoXOz?editors=1011 this CodePen}
         */
        document.addEventListener('click', handleClickOutside);
      }, 0);
    }
    return () => {
      setTimeout(() => {
        document.removeEventListener('click', handleClickOutside);
      }, 0);
    };
  }, [disabled, ref]);
  return onClickOutside;
}

/**
 * Run callback when a keydown event occurs inside of the provided `ref`.
 * Useful for example for creating custom focus handling functionality
 * (e.g. 'Use arrow keys to navigate calendar').
 *
 * @example
 *
 * const [open, setOpen] = useState(true);
 * const ref = useRef<HTMLDialogElement>(null);
 *
 * const onKeyDownInside = useOnKeyDownInside(ref, !open); // Note: second argument is optional.
 * onKeyDownInside((event) => {
 *   console.log("You keydown'ed inside the open calendar");
 *   console.log(event); // The original `KeyboardEvent`.
 *
 *   switch (event.key) {
 *     case 'ArrowRight':
 *       event.preventDefault();
 *       document.activeElement?.nextElementSibling?.focus();
 *       break;
 *     case 'ArrowLeft':
 *       event.preventDefault();
 *       document.activeElement?.previousElementSibling?.focus();
 *       break;
 *   }
 * });
 *
 * ...
 *
 * <Calendar ref={ref} open={open}>
 */
function useOnKeyDownInside(ref, disabled = false) {
  const cb = useRef();
  function onKeyDownInside(callback) {
    cb.current = callback;
  }
  useEffect(() => {
    if (!document) return;
    function handleKeyDownInside(event) {
      var _a;
      if (ref.current && (event === null || event === void 0 ? void 0 : event.target).isConnected && ref.current.contains(event === null || event === void 0 ? void 0 : event.target)) {
        (_a = cb.current) === null || _a === void 0 ? void 0 : _a.call(cb, event);
      }
    }
    if (!disabled) {
      setTimeout(() => {
        /**
         * By wrapping with a timeout, we ensure that any external keydown logic
         * (e.g. 'keydown this button to show/hide a dialog') has the chance to
         * run before we try to act on the keydown. Failing to do so could cause
         * the inside keydown to interfere with the external keydown logic.
         * For a more detailed explanation, see
         * {@link https://codepen.io/olefjaerestad/pen/XWwoXOz?editors=1011 this CodePen}
         */
        document.addEventListener('keydown', handleKeyDownInside);
      }, 0);
    }
    return () => {
      setTimeout(() => {
        document.removeEventListener('keydown', handleKeyDownInside);
      }, 0);
    };
  }, [disabled, ref]);
  return onKeyDownInside;
}

/**
 * Run callback when a keyup event occurs outside of the provided `ref`.
 * Useful for example for closing dialogs or tooltips when tabbing past them.
 *
 * Note: If you have an "open dialog" button that opens a dialog on keyup,
 * keyup'ing this would by default register as a keyup outside the dialog, and
 * potentially close the dialog before it's ever opened. To prevent this, use
 * the optional `disabled` parameter to control when the outside keyup handler
 * should be enabled.
 *
 * @example
 *
 * const [open, setOpen] = useState(true);
 * const ref = useRef<HTMLDialogElement>(null);
 *
 * const onKeyUpOutside = useOnKeyUpOutside(ref, !open); // Note: second argument is optional.
 * onKeyUpOutside((event) => {
 *   console.log("You keyup'ed outside the open dialog");
 *   console.log(event); // The original `KeyboardEvent`.
 *   setOpen(false);
 * });
 *
 * ...
 *
 * <dialog ref={ref} open={open}>
 */
function useOnKeyUpOutside(ref, disabled = false) {
  const cb = useRef();
  function onKeyUpOutside(callback) {
    cb.current = callback;
  }
  useEffect(() => {
    if (!document) return;
    function handleKeyUpOutside(event) {
      var _a;
      if (ref.current && (event === null || event === void 0 ? void 0 : event.target).isConnected && !ref.current.contains(event === null || event === void 0 ? void 0 : event.target)) {
        (_a = cb.current) === null || _a === void 0 ? void 0 : _a.call(cb, event);
      }
    }
    if (!disabled) {
      setTimeout(() => {
        /**
         * By wrapping with a timeout, we ensure that any external keyup logic
         * (e.g. 'keyup this button to show/hide a dialog') has the chance to
         * run before we try to act on the keyup. Failing to do so could cause
         * the outside keyup to interfere with the external keyup logic.
         * For a more detailed explanation, see
         * {@link https://codepen.io/olefjaerestad/pen/XWwoXOz?editors=1011 this CodePen}
         */
        document.addEventListener('keyup', handleKeyUpOutside);
      }, 0);
    }
    return () => {
      setTimeout(() => {
        document.removeEventListener('keyup', handleKeyUpOutside);
      }, 0);
    };
  }, [disabled, ref]);
  return onKeyUpOutside;
}

/**
 * Run callback when a parent form of `ref` is reset.
 *
 * @example
 *
 * ```tsx
 * const ref = useRef<HTMLInputElement>(null);
 *
 * const onParentFormReset = useOnParentFormReset(ref);
 * onKeyUpOutside((event: Event) => {
 *   console.log("Parent form of `ref` was reset!");
 *   console.log(event); // The original `Event`.
 * });
 *
 * <input ref={ref} />
 * ```
 */
function useOnParentFormReset(ref) {
  const cb = useRef();
  function onParentFormReset(callback) {
    cb.current = callback;
  }
  useEffect(() => {
    if (!document) return;
    function handleParentFormReset(event) {
      var _a, _b, _c;
      if (ref.current && ((_a = event.target) === null || _a === void 0 ? void 0 : _a.isConnected) && ((_b = event.target) === null || _b === void 0 ? void 0 : _b.contains(ref.current))) {
        (_c = cb.current) === null || _c === void 0 ? void 0 : _c.call(cb, event);
      }
    }
    document.addEventListener('reset', handleParentFormReset);
    return () => document.removeEventListener('reset', handleParentFormReset);
  }, [ref]);
  return onParentFormReset;
}

/**
 * Get the `RelativeRect` of an element, relative to another element. The
 * difference between a `RelativeRect` and a `DOMRect` is that a `RelativeRect`
 * is relative to another DOM element, while a `DOMRect` is relative to the
 * viewport.
 *
 * Use the returned functions to calculate the `RelativeRect` when needed.
 *
 * @example
 *
 * ```tsx
 * const relativeToRef = useRef<HTMLDivElement>(null);
 * const elRef = useRef<HTMLDivElement>(null);
 * const { calculate, rect, reset } = useRelativeRect();
 *
 * return (
 *   <div ref={relativeToRef}>
 *     <button onClick={() => calculate(elRef.current, relativeToRef.current)}>Calculate `RelativeRect`</button>
 *     <button onClick={reset}>Reset `RelativeRect`</button>
 *     <div ref={elRef}>
 *       <p>`elRef`'s left side is {rect.left} pixels to the right of `relativeToRef`'s left side.</p>
 *       <p>`elRef`'s right side is {rect.right} pixels to the left of `relativeToRef`'s right side.</p>
 *     <pre>{JSON.stringify(rect, null, 2)}</pre>
 *   </div>
 * );
 * ```
 */
function useRelativeRect() {
  const [rect, setRect] = useState({
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });
  const calculate = useCallback((el, relativeTo) => {
    const rect = relativeRect(el, relativeTo);
    setRect(rect);
  }, []);
  const reset = useCallback(() => {
    setRect({
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      x: 0,
      y: 0,
      width: 0,
      height: 0
    });
  }, []);
  return {
    calculate,
    rect,
    reset
  };
}
/**
 * Returns the `RelativeRect` object of the provided element `el`.
 * The difference between a `RelativeRect` and a `DOMRect` is that the
 * `RelativeRect` is relative to the provided element `relativeTo`, while the
 * `DOMRect` is relative to the viewport.
 */
function relativeRect(el, relativeTo) {
  const elRect = el.getBoundingClientRect();
  const relativeRect = relativeTo.getBoundingClientRect();
  const top = elRect.top - relativeRect.top;
  const bottom = relativeRect.bottom - elRect.bottom;
  const left = elRect.left - relativeRect.left;
  const right = relativeRect.right - elRect.right;
  const width = elRect.width;
  const height = elRect.height;
  return {
    top,
    bottom,
    right,
    left,
    width,
    height,
    x: left,
    y: top
  };
}

/**
 * Object bracket notation removes type safety. This function restores it.
 */
function objKey(obj, key) {
  return obj[key];
}

/**
 * Determines if the code is running in a server-side rendering (SSR) environment.
 */
function isSSR() {
  return typeof document === 'undefined';
}

function isDefined(value) {
  return value !== undefined && value !== null;
}

export { AnchoredPopover, HiddenInput, dispatchEvent, isDefined, isSSR, mergeRefs, objKey, printDeprecatedComponentWarning, printDeprecatedPropWarning, printUnsupportedPropWarning, relativeRect, throttle, transformXY, traverseToSibling, unextendStyle, useMergeRefs, useOnClickOutside, useOnKeyDownInside, useOnKeyUpOutside, useOnParentFormReset, useRelativeRect, useShowDialog, when };
